import { URLKeys, noddiAsync } from 'noddi-async';
import type { ErrorCodes } from 'noddi-ui-web';
import { ApiErrorMessage, NoddiButton, NoddiOtp, NoddiPasswordStrength, useAppForm } from 'noddi-ui-web';
import { z } from 'zod';
import { OTP_LENGTH, createOtpPasswordSchema } from './passwordResetSchemas';
import { ResetCountdown } from './ResetCountdown';

export type PasswordResetOtpConfirmTranslations = {
  enterCodeAndPassword: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
  resetPassword: string;
  cancel: string;
  passwordRequired: string;
  passwordMinLength: string;
  confirmPasswordRequired: string;
  passwordsDontMatch: string;
  codeRequired: string;
  codeExpiresIn: string;
  codeExpired: string;
};

type PasswordResetOtpConfirmProps = {
  sessionId: string;
  expiresInSeconds?: number;
  onSuccess: () => void;
  onCancel: () => void;
  translations?: PasswordResetOtpConfirmTranslations;
  errorCodes?: ErrorCodes;
};

const defaultTexts: PasswordResetOtpConfirmTranslations = {
  enterCodeAndPassword: 'Enter the code from your SMS and choose a new password',
  code: 'Code',
  newPassword: 'New password',
  confirmPassword: 'Confirm password',
  resetPassword: 'Reset password',
  cancel: 'Cancel',
  passwordRequired: 'Password is required',
  passwordMinLength: 'Password must be at least 12 characters',
  confirmPasswordRequired: 'Please confirm your password',
  passwordsDontMatch: "Passwords don't match",
  codeRequired: 'Enter the 6-digit code',
  codeExpiresIn: 'The code expires in',
  codeExpired: 'The code has expired. Request a new one to continue.'
};

export const PasswordResetOtpConfirm = ({
  sessionId,
  expiresInSeconds,
  onSuccess,
  onCancel,
  translations,
  errorCodes
}: PasswordResetOtpConfirmProps) => {
  const texts = translations ?? defaultTexts;

  const { mutateAsync: confirmOtp, error } = noddiAsync.usePost({
    type: URLKeys.postPasswordResetConfirmOtp,
    queryConfig: {
      onSuccess
    }
  });

  const validationSchema = createOtpPasswordSchema(texts);
  type FormValues = z.input<typeof validationSchema>;

  const form = useAppForm({
    defaultValues: { password: '', confirmPassword: '', otpCode: '' } satisfies FormValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      await confirmOtp({ sessionId, otpCode: value.otpCode, newPassword: value.password });
    }
  });

  return (
    <div className='flex flex-col gap-4'>
      <p className='text-sm text-muted-foreground'>{texts.enterCodeAndPassword}</p>
      {expiresInSeconds !== undefined && (
        <ResetCountdown seconds={expiresInSeconds} label={texts.codeExpiresIn} expiredLabel={texts.codeExpired} />
      )}
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-4'>
            <form.AppField name='otpCode'>
              {(field) => (
                <div className='flex flex-col gap-1'>
                  <span className='text-sm'>{texts.code}</span>
                  <NoddiOtp value={field.state.value} onChange={field.handleChange} maxLength={OTP_LENGTH} />
                </div>
              )}
            </form.AppField>
            <form.AppField name='password'>
              {(field) => (
                <div className='flex flex-col gap-1'>
                  <field.Input type='password' label={texts.newPassword} autoComplete='new-password' />
                  <NoddiPasswordStrength password={field.state.value} />
                </div>
              )}
            </form.AppField>
            <form.AppField name='confirmPassword'>
              {(field) => <field.Input type='password' label={texts.confirmPassword} autoComplete='new-password' />}
            </form.AppField>
            {error && errorCodes && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
            <div className='flex gap-2'>
              <NoddiButton type='button' variant='outline' onClick={onCancel} className='flex-1'>
                {texts.cancel}
              </NoddiButton>
              <form.SubscribeButton label={texts.resetPassword} className='flex-1' />
            </div>
          </div>
        </form>
      </form.AppForm>
    </div>
  );
};
